import type { ReactNode } from "react";
import { ArrowUpRight, FileText, CalendarDays } from "lucide-react";
import type { RescueCase } from "@io-spoke/contracts/rescue";
import { Badge } from "../ui/badge";

const statusCopy: Record<RescueCase["status"], string> = {
  detected: "Needs review",
  active: "Recovery started",
  saved: "Saved",
  lost: "Lost",
};

const sourceColumns: Record<number, string> = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
};

type Props = {
  rescueCase: RescueCase;
  onOpenEvidence: (id: string) => void;
  actions?: ReactNode;
};

function dateLabel(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function accountValue(rescueCase: RescueCase) {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: rescueCase.currency,
      maximumFractionDigits: 0,
    }).format(rescueCase.annualValueCents / 100);
  } catch {
    return `${rescueCase.annualValueCents / 100} ${rescueCase.currency}`;
  }
}

export function RescueCaseBriefing({
  rescueCase,
  onOpenEvidence,
  actions,
}: Props) {
  const dated = rescueCase.evidence.map((item, index) => ({
    item,
    index,
    time: Date.parse(item.occurredAt),
  }));
  dated.sort((a, b) => {
    if (!Number.isFinite(a.time) && !Number.isFinite(b.time)) {
      return a.index - b.index;
    }
    if (!Number.isFinite(a.time)) return 1;
    if (!Number.isFinite(b.time)) return -1;
    return a.time - b.time;
  });
  const sources = dated.slice(0, 3);
  const sourceCount =
    sources.length < rescueCase.evidence.length
      ? `${sources.length} of ${rescueCase.evidence.length} linked`
      : `${rescueCase.evidence.length} linked`;
  const sourceOrder = dated.some((source) => !Number.isFinite(source.time))
    ? "recorded sources"
    : "source chronology";
  const renewal = dateLabel(rescueCase.renewalAt);

  return (
    <header className="relative isolate overflow-hidden border-b bg-gradient-to-br from-orange-500/[0.07] via-card to-card px-5 pb-6 pt-6 sm:px-8 sm:pt-8">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 -top-24 -z-10 h-64 w-64 rounded-full border-[32px] border-orange-500/[0.04]"
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-orange-800 dark:text-orange-300">
          <span
            className="h-1.5 w-1.5 rounded-full bg-orange-600"
            aria-hidden="true"
          />
          Customer briefing
        </p>
        <Badge
          variant="outline"
          className="border-orange-700/20 bg-card/70 text-orange-900 dark:border-orange-300/20 dark:text-orange-200"
        >
          {statusCopy[rescueCase.status]}
        </Badge>
      </div>
      <h2
        id="guided-case-title"
        className="mt-5 max-w-2xl break-words text-3xl font-semibold leading-tight tracking-tight sm:text-4xl"
      >
        {rescueCase.customerName}
      </h2>
      <p className="mt-3 max-w-2xl break-words text-base font-medium leading-relaxed text-foreground/90 sm:text-lg">
        {rescueCase.title}
      </p>
      <div className="my-5 h-px w-12 bg-orange-600/50" aria-hidden="true" />
      <p className="text-xs font-medium text-muted-foreground">
        The commitment
      </p>
      <p className="mt-1 max-w-2xl break-words text-sm leading-6 text-foreground/90">
        {rescueCase.promise}
      </p>
      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
        <p className="font-medium tabular-nums">
          {accountValue(rescueCase)} annual account value
        </p>
        {renewal ? (
          <p className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
            Renewal {renewal}
          </p>
        ) : null}
      </div>
      {actions}
      {sources.length ? (
        <div className="mt-7 border-t border-border/70 pt-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold">
              Sources behind the assessment
            </h3>
            <p className="text-[11px] text-muted-foreground">
              {sourceCount} · {sourceOrder}
            </p>
          </div>
          <ol
            className={`grid gap-1 sm:gap-3 ${sourceColumns[sources.length]}`}
          >
            {sources.map(({ item }, index) => (
              <li key={item.id} className="relative min-w-0">
                {index < sources.length - 1 ? (
                  <span
                    aria-hidden="true"
                    className="absolute left-3 top-7 h-full w-px bg-border sm:hidden"
                  />
                ) : null}
                <button
                  type="button"
                  onClick={() => onOpenEvidence(item.id)}
                  aria-label={`Review source: ${item.title}`}
                  className="group relative flex min-h-11 w-full items-start gap-3 rounded-lg py-2 text-left outline-none transition-colors hover:bg-orange-500/5 focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
                >
                  <span className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full border bg-card text-orange-800 dark:text-orange-300">
                    <FileText className="h-3 w-3" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1 pb-2">
                    <span className="block text-[11px] text-muted-foreground">
                      {dateLabel(item.occurredAt) ?? "Undated source"}
                    </span>
                    <span className="mt-1 block break-words text-xs font-medium leading-5">
                      {item.channel} · {item.title}
                    </span>
                    <span
                      className="mt-1 block truncate text-[11px] text-muted-foreground"
                      title={item.sourceLabel}
                    >
                      {item.sourceLabel}
                    </span>
                  </span>
                  <ArrowUpRight
                    className="mr-1 mt-1 h-3 w-3 shrink-0 text-muted-foreground group-hover:text-foreground"
                    aria-hidden="true"
                  />
                </button>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <p className="mt-5 text-xs text-muted-foreground">
          No linked sources yet.
        </p>
      )}
    </header>
  );
}
